import React, { useEffect, useState } from 'react';
import { Button, Grid, makeStyles } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { useDispatch, useSelector } from 'react-redux';
import _isNaN from 'lodash/isNaN';
import _isNil from 'lodash/isNil';
import Modal from '../common/Modal';
import Steps from '../common/Steps';
import { showProgress } from '../../redux/actions/DealershipActions';
import { apiEndpoints } from '../../constants/apiEndpoints';
import service from '../../apis/service';
import { ErrorToast, MultipleErrorToast, notifySuccess } from '../utils/Utils';
import { QuestionList } from '../common/QuestionList';
import { AddQuestion } from '../common/AddQuestion';
import { ExamInfo } from '../common/QuestionInfo';
import { GET_INITIAL_OPTIONS } from '../../constants/exam';
import useSelectedCertInfo from './hooks/useSelectedCertInfo';

const useStyle = makeStyles({
  popupContainer: {
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
  },
  title: { fontWeight: '700', fontSize: '18px' },
  subTitle: { fontWeight: '500', fontSize: '12px', color: 'rgba(0, 0, 0, 0.4)' },
});

export function AddExamPopup({ isOpenAddExam, setIsOpenAddExam, isEdit }) {
  const classes = useStyle();
  const dispatch = useDispatch();
  const fetchSelectedCertInfo = useSelectedCertInfo();
  const selectedCertInfo = useSelector((state) => state.certificateData.selectedCertInfo);

  const [step, setStep] = useState(1);
  const [journeys, setJourneys] = useState([]);
  const [categories, setCategories] = useState([]);
  const [guides, setGuides] = useState([]);
  const [selectedGuideId, setSelectedGuideId] = useState('');
  const [selectedJId, setSelectedJId] = useState('');
  const [selectedCatId, setSelectedCatId] = useState('');
  const [isAddQuestion, setIsAddQuestion] = useState(false);
  const [queType, setQueType] = useState('single_select');
  const [isQEdit, setIsQEdit] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [examInfo, setExamInfo] = useState({
    numberQuestions: '',
    duration: '',
    passingScore: '',
    distinctionScore: '',
    totalScore: '',
    instruction: '',
    lockPeriod: 1,
  });

  const [questions, setquestions] = useState([]);
  const [queTitle, setQueTitle] = useState('');
  const [res, setRes] = useState({});
  const [options, setOptions] = useState(GET_INITIAL_OPTIONS);
  const [timeUnit, setTimeUnit] = useState('days');
  const LOCK_TIME_UNITS = ['hours', 'days'];

  const isDecimal = (value) => typeof value === 'string' && value.includes('.');

  const parseLockTime = (result) => {
    const DEFAULT = { lockPeriod: 1, lockTimeUnit: 'days' };

    if (_isNil(result)) return DEFAULT;

    if (result.reAttemptLockTime) {
      const parts = result.reAttemptLockTime.trim().split(' ');
      if (parts.length < 2) return DEFAULT;

      const lockPeriod = parseInt(parts[0], 10);
      if (_isNaN(lockPeriod)) return DEFAULT;

      let lockTimeUnit = parts[1].toLowerCase();
      if (lockTimeUnit === 'day') lockTimeUnit = 'days';
      if (lockTimeUnit === 'hour') lockTimeUnit = 'hours';

      return { lockPeriod, lockTimeUnit };
    }

    if (!_isNil(result.lockPeriod)) {
      return {
        lockPeriod: result.lockPeriod,
        lockTimeUnit: result.lockTimeUnit || 'days',
      };
    }

    return DEFAULT;
  };

  const getExamDetail = async () => {
    dispatch(showProgress(true));

    try {
      const result = await service.get(apiEndpoints.CERT_EXAM_DETAIL.replace('ID', selectedCertInfo.id));
      setquestions(result.questions || []);

      const { lockPeriod, lockTimeUnit } = parseLockTime(result);

      const info = {
        numberQuestions: result.numberQuestions,
        duration: result.duration,
        passingScore: result.passingScore,
        distinctionScore: result.distinctionScore,
        instruction: result.instructions,
        totalScore: result.totalScore,
        lockPeriod,
        editWarning: result.editWarning ? result.editWarning : undefined,
        questionEditRemoveWarning: result.questionEditRemoveWarning ? result.questionEditRemoveWarning : undefined,
      };
      setExamInfo(info);
      setRes(info);
      setTimeUnit(lockTimeUnit);
    } catch (error) {
      ErrorToast(error);
    }
    dispatch(showProgress(false));
  };

  useEffect(() => {
    if (isEdit && selectedCertInfo?.id && selectedCertInfo?.exam?.id) {
      getExamDetail();
    }
  }, [selectedCertInfo]);

  useEffect(() => {
    if (selectedJId) {
      const selectedJ = journeys.filter((j) => j.id === selectedJId)[0];
      const guideList = selectedJ ? selectedJ.guides || [] : [];
      setGuides(guideList);
    }
  }, [selectedJId]);

  const checkValidation = () => {
    const errors = [];
    let isValid = true;

    if (!selectedCatId) {
      isValid = false;
      errors.push('Please select a category');
    }
    if (!selectedJId) {
      isValid = false;
      errors.push('Please select a journey');
    }
    if (!queTitle || queTitle.trim() === '') {
      isValid = false;
      errors.push('Please enter a question');
    }

    const emptyOptions = options?.filter((op) => !op.title || op.title.trim() === '') || [];
    if (emptyOptions?.length > 0) {
      isValid = false;
      errors.push('Please enter all the options');
    }

    const answerOptions = options?.filter((op) => op.isAnswer) || [];
    if (answerOptions?.length === 0 && queType === 'single_select') {
      isValid = false;
      errors.push('Please select correct option for the question');
    }
    if (answerOptions?.length < 2 && queType === 'multi_select') {
      isValid = false;
      errors.push('Please select atleast two correct options for the question or change the question type');
    }

    if (errors.length !== 0) {
      MultipleErrorToast(errors);
    }
    return isValid;
  };

  const onAdd = () => {
    if (!checkValidation()) return;
    const temp = [...questions];
    const que = {
      title: queTitle,
      type: queType,
      journeyId: selectedJId,
      questionCategoryId: selectedCatId,
      options,
    };
    if (selectedGuideId) {
      que.guideId = selectedGuideId;
    }
    if (isQEdit) {
      const editedQuestion = temp[selectedIndex];
      if (editedQuestion.id && editedQuestion.type === queType) {
        que.id = editedQuestion.id;
      }
      temp[selectedIndex] = que;
      setquestions(temp);
    } else {
      setquestions([...temp, que]);
    }

    clearState();
    setIsAddQuestion(false);
  };

  const createExam = async () => {
    dispatch(showProgress(true));

    const { lockPeriod } = examInfo;
    const reAttemptLockTimeTemp = lockPeriod
      ? `${lockPeriod} ${lockPeriod <= 1 ? timeUnit.replace(/s$/, '') : timeUnit}`
      : '';
    const data = {
      certificationId: selectedCertInfo.id,
      numberQuestions: examInfo.numberQuestions,
      duration: examInfo.duration,
      passingScore: examInfo.passingScore,
      totalScore: 100,
      distinctionScore: examInfo.distinctionScore,
      instructions: examInfo.instruction,
      reAttemptLockTime: reAttemptLockTimeTemp || undefined,
      questions,
    };
    try {
      await service.post(apiEndpoints.CERT_EXAM_CREATE, data);
      notifySuccess('Exam created successfully');
      fetchSelectedCertInfo();
      setIsOpenAddExam(false);
    } catch (error) {
      ErrorToast(error);
    }

    dispatch(showProgress(false));
  };

  const updateExam = async () => {
    dispatch(showProgress(true));
    const { lockPeriod } = examInfo;
    const reAttemptLockTimeTemp = lockPeriod
      ? `${lockPeriod} ${lockPeriod <= 1 ? timeUnit.replace(/s$/, '') : timeUnit}`
      : '';
    const data = {
      certificationId: selectedCertInfo.id,
      examId: selectedCertInfo.exam.id,
      numberQuestions: examInfo.numberQuestions,
      duration: examInfo.duration,
      passingScore: examInfo.passingScore,
      totalScore: 100,
      distinctionScore: examInfo.distinctionScore,
      instructions: examInfo.instruction,
      reAttemptLockTime: reAttemptLockTimeTemp || undefined,
      questions,
    };
    try {
      await service.post(apiEndpoints.CERT_EXAM_UPDATE, data);
      notifySuccess('Exam updated successfully');
      fetchSelectedCertInfo();
      setIsOpenAddExam(false);
    } catch (error) {
      ErrorToast(error);
    }

    dispatch(showProgress(false));
  };

  const handleValidation = () => {
    const errors = [];
    let isValid = true;
    if (step === 1 && !res?.editWarning) {
      if (examInfo.numberQuestions === '' || _isNil(examInfo.numberQuestions)) {
        isValid = false;
        errors.push('Please enter total questions');
      }
      if (examInfo.numberQuestions && Number.isNaN(Number(examInfo.numberQuestions))) {
        isValid = false;
        errors.push('Total question must be a number');
      } else if (examInfo.numberQuestions && Number(examInfo.numberQuestions) <= 0) {
        isValid = false;
        errors.push('Total questions must be greater than or equal to 1');
      }

      if (!examInfo.duration) {
        isValid = false;
        errors.push('Please enter a duration');
      }
      if (examInfo.duration && Number.isNaN(Number(examInfo.duration))) {
        isValid = false;
        errors.push('Duration must be a number');
      } else if (examInfo.duration && examInfo.duration <= 0) {
        isValid = false;
        errors.push('Duration must be greater than or equal to 1');
      }
      if (!examInfo.passingScore) {
        isValid = false;
        errors.push('Please enter passing score');
      }
      if (examInfo.passingScore && Number.isNaN(Number(examInfo.passingScore))) {
        isValid = false;
        errors.push('Passing score must be a number');
      } else if (examInfo.passingScore && examInfo.passingScore > 100) {
        isValid = false;
        errors.push('Passing Score must be less than or equal to 100');
      } else if (examInfo.passingScore && examInfo.passingScore <= 0) {
        isValid = false;
        errors.push('Passing Score must be greater than or equal to 1');
      }

      if (!examInfo.distinctionScore) {
        isValid = false;
        errors.push('Please enter distinction score');
      }
      if (examInfo.distinctionScore && Number.isNaN(Number(examInfo.distinctionScore))) {
        isValid = false;
        errors.push('Distinction score must be a number');
      } else if (examInfo.distinctionScore && examInfo.distinctionScore > 100) {
        isValid = false;
        errors.push('Distinction Score must be less than or equal to 100');
      } else if (examInfo.distinctionScore && examInfo.distinctionScore <= 0) {
        isValid = false;
        errors.push('Distinction Score must be greater than or equal to 1');
      }
      if (!examInfo.instruction || examInfo.instruction.trim() === '') {
        isValid = false;
        errors.push('Please enter instruction');
      }
      if (examInfo.lockPeriod === '' || isDecimal(examInfo.lockPeriod)) {
        isValid = false;
        errors.push('Please enter valid lock period');
      } else if (!_isNil(examInfo.lockPeriod)) {
        const lockPeriodNum = Number(examInfo.lockPeriod);
        if (_isNaN(lockPeriodNum)) {
          isValid = false;
          errors.push('Lock Period must be a number');
        } else if (lockPeriodNum < 0) {
          isValid = false;
          errors.push('Please enter valid lock period.');
        }
      }
    }
    if (step === 1 && res?.editWarning) {
      if (!examInfo.instruction || examInfo.instruction.trim() === '') {
        isValid = false;
        errors.push('Please enter instruction');
      }
      if (
        examInfo.numberQuestions !== res.numberQuestions ||
        examInfo.duration !== res.duration ||
        examInfo.passingScore !== res.passingScore ||
        res.distinctionScore !== examInfo.distinctionScore
      ) {
        isValid = false;
        errors.push(res.editWarning);
        const info = { ...examInfo };

        // eslint-disable-next-line no-unused-expressions
        info.numberQuestions = res.numberQuestions;
        info.duration = res.duration;
        info.passingScore = res.passingScore;
        info.distinctionScore = res.distinctionScore;
        info.totalScore = res.totalScore;

        setExamInfo(info);
      }
    }
    if (errors.length !== 0) {
      MultipleErrorToast(errors);
    }
    return isValid;
  };

  const onNext = () => {
    if (!handleValidation()) return;

    if (step < 2) {
      setStep((pre) => pre + 1);
    }
    if (step === 2) {
      if (examInfo.numberQuestions > questions.length) {
        ErrorToast(
          'Total Questions in Step 1 should be less than or equal to the number of questions added in Step 2.',
        );
        return;
      }
      if (isEdit) {
        updateExam();
      } else {
        createExam();
      }
    }
  };

  const onBack = () => {
    if (step === 2 && isAddQuestion) {
      setIsAddQuestion(false);
      clearState();
    } else {
      setStep((pre) => pre - 1);
    }
  };

  const clearState = () => {
    setSelectedCatId('');
    setSelectedGuideId('');
    setSelectedJId('');
    setQueTitle('');
    setQueType('single_select');
    setOptions(GET_INITIAL_OPTIONS);
    setSelectedIndex(null);
    setIsQEdit(false);
  };

  const getExamtJourney = async () => {
    dispatch(showProgress(true));
    try {
      const result = await service.get(apiEndpoints.CERT_EXAM_JOURNEYS.replace('ID', selectedCertInfo.id));
      setJourneys(result);
    } catch (error) {
      ErrorToast(error);
    }
    dispatch(showProgress(false));
  };
  const getExamQueCat = async () => {
    dispatch(showProgress(true));
    try {
      const result = await service.get(apiEndpoints.CERT_EXAM_QUE_CAT);
      setCategories(result);
    } catch (error) {
      ErrorToast(error);
    }
    dispatch(showProgress(false));
  };

  useEffect(() => {
    if (selectedCertInfo.id) {
      getExamtJourney();
      getExamQueCat();
    }
  }, [selectedCertInfo]);

  return (
    <Modal isOpen={isOpenAddExam} height="90vh" width="950px">
      <Grid className={classes.popupContainer}>
        <Grid container direction="row" justifyContent="space-between" alignItems="flex-start">
          <Grid item>
            <Grid container direction="column" spacing={1}>
              <Grid item className={classes.title}>
                {isEdit ? 'Edit Exam' : 'Add Exam'}
              </Grid>
              <Grid item className={classes.subTitle}>
                {isEdit ? `ID: ${selectedCertInfo?.exam?.id || ''}` : ''}
              </Grid>
            </Grid>
          </Grid>
          <Grid item>
            <CloseIcon onClick={() => setIsOpenAddExam(false)} style={{ cursor: 'pointer' }} />
          </Grid>
        </Grid>

        <Steps step={step} stepInfo={['Exam Info', 'Questions']} />

        {step === 1 && (
          <ExamInfo
            examInfo={examInfo}
            setExamInfo={setExamInfo}
            calledFrom="certificate"
            LOCK_TIME_UNITS={LOCK_TIME_UNITS}
            timeUnit={timeUnit}
            setTimeUnit={setTimeUnit}
          />
        )}
        {step === 2 && !isAddQuestion && (
          <QuestionList
            setIsAddQuestion={setIsAddQuestion}
            questions={questions}
            setQeustions={setquestions}
            setIsEdit={setIsQEdit}
            setSelectedIndex={setSelectedIndex}
            categories={categories}
            calledFrom="certificate"
            info={res}
          />
        )}

        {step === 2 && isAddQuestion && (
          <AddQuestion
            journeys={journeys}
            guides={guides}
            categories={categories}
            setSelectedGuideId={setSelectedGuideId}
            setSelectedCatId={setSelectedCatId}
            setSelectedJId={setSelectedJId}
            setIsAddQuestion={setIsAddQuestion}
            selectedJId={selectedJId}
            selectedCatId={selectedCatId}
            selectedGuideId={selectedGuideId}
            queType={queType}
            setQueType={setQueType}
            queTitle={queTitle}
            setQueTitle={setQueTitle}
            options={options}
            setOptions={setOptions}
            isEdit={isQEdit}
            questions={questions}
            selectedIndex={selectedIndex}
            calledFrom="certificate"
            info={examInfo}
          />
        )}

        <Grid style={{ display: 'flex' }}>
          {step > 1 && (
            <Button onClick={onBack} variant="outlined">
              Back
            </Button>
          )}
          <Grid container direction="row" justifyContent="flex-end" alignItems="center">
            <>
              <Button onClick={() => setIsOpenAddExam(false)} variant="outlined">
                Cancel
              </Button>
              {step === 2 && isAddQuestion ? (
                <Button variant="contained" color="primary" style={{ marginLeft: '10px' }} onClick={onAdd}>
                  Add
                </Button>
              ) : (
                <Button variant="contained" color="primary" style={{ marginLeft: '10px' }} onClick={onNext}>
                  {step !== 2 ? 'Next' : 'Save'}
                </Button>
              )}
            </>
          </Grid>
        </Grid>
      </Grid>
    </Modal>
  );
}
